import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, X, Clock, MapPin, User, Lock } from 'lucide-react';
import { Card, BigCalendar, DatePickerInput } from '@components/ui';
import { getEventsByUser, eventCategories, getUpcomingEvents } from '@mocks/programDefteri';
import clsx from 'clsx';

// Kategori renk haritası (BigCalendar için)
const eventColors = {
    tapu: { bgColor: '#3B82F6', textColor: '#ffffff' },      // blue
    musteri: { bgColor: '#F59E0B', textColor: '#ffffff' },   // amber
    toplanti: { bgColor: '#10B981', textColor: '#ffffff' },  // green
    teslimat: { bgColor: '#8B5CF6', textColor: '#ffffff' },  // purple
    diger: { bgColor: '#6B7280', textColor: '#ffffff' },     // gray
};

export default function Schedule() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const { user } = useSelector((state) => state.auth);

    const constructionId = showAllConstructions ? null : selectedConstruction?.id;
    const userId = user?.id ?? null;

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [calendarDate, setCalendarDate] = useState(new Date());

    // Yeni etkinlik form state
    const [newEvent, setNewEvent] = useState({
        title: '',
        date: new Date(),
        time: '10:00',
        category: 'toplanti',
        description: '',
    });

    // Kullanıcıya ve inşaata göre kişisel etkinlikler
    const rawEvents = getEventsByUser(constructionId, userId);
    const upcomingEvents = getUpcomingEvents(rawEvents, 3);

    // BigCalendar formatına dönüştür
    const calendarEvents = useMemo(() => {
        return rawEvents.map(event => {
            const [hours, minutes] = event.time.split(':');
            const start = new Date(event.date);
            start.setHours(parseInt(hours), parseInt(minutes));
            const end = new Date(start);
            end.setHours(end.getHours() + 1);

            return {
                id: event.id,
                title: event.title,
                start,
                end,
                category: event.category,
                description: event.description,
                createdBy: event.createdBy,
                allDay: false,
                resource: event,
            };
        });
    }, [rawEvents]);

    // Etkinlik tıklama
    const handleSelectEvent = useCallback((event) => {
        setSelectedEvent(event.resource);
    }, []);

    // Boş slot tıklama (yeni etkinlik için)
    const handleSelectSlot = useCallback((slotInfo) => {
        setNewEvent(prev => ({
            ...prev,
            date: slotInfo.start,
        }));
        setShowAddModal(true);
    }, []);

    const getCategoryLabel = (key) => eventCategories.find(c => c.key === key)?.label || key;
    const getCategoryColor = (key) => eventCategories.find(c => c.key === key)?.color || 'bg-neutral-400';

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
        });
    };

    const handleSaveEvent = () => {
        if (!newEvent.title.trim()) {
            toast.error('Etkinlik başlığı girin');
            return;
        }
        toast.success('Etkinlik başarıyla eklendi');
        setNewEvent({ title: '', date: new Date(), time: '10:00', category: 'toplanti', description: '' });
        setShowAddModal(false);
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Program Defteri</h1>
                    <p className="text-text-tertiary mt-1 flex items-center gap-1.5">
                        <Lock size={13} className="text-text-muted" />
                        <span>
                            {showAllConstructions
                                ? 'Tüm inşaatlar — Kişisel takvim'
                                : `${selectedConstruction?.name || ''} — Kişisel takvim`}
                        </span>
                    </p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                    <Plus size={18} />
                    <span className="font-medium">Etkinlik Ekle</span>
                </button>
            </div>

            {/* Kişisel takvim bilgi notu */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/15 rounded-lg text-sm text-primary">
                <Lock size={14} />
                <span>Bu takvim yalnızca size aittir. Aynı projeye erişen diğer kullanıcılar kendi takvimlerini görür.</span>
            </div>

            {/* Big Calendar */}
            <BigCalendar
                events={calendarEvents}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                onNavigate={setCalendarDate}
                defaultView="month"
                defaultDate={calendarDate}
                eventColors={eventColors}
                height="550px"
                selectable
            />

            {/* Kategori Legend */}
            <div className="flex flex-wrap items-center gap-4">
                {eventCategories.map((cat) => (
                    <div key={cat.key} className="flex items-center gap-2">
                        <div className={clsx("w-3 h-3 rounded-full", cat.color)} />
                        <span className="text-sm text-text-secondary">{cat.label}</span>
                    </div>
                ))}
            </div>

            {/* Yaklaşan Etkinlikler */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <Clock size={20} className="text-primary" />
                    Yaklaşan Etkinliklerim
                </h3>
                {upcomingEvents.length === 0 ? (
                    <p className="text-text-muted text-sm text-center py-4">Yaklaşan etkinlik bulunmuyor.</p>
                ) : (
                    <div className="space-y-3">
                        {upcomingEvents.map((event) => (
                            <button
                                key={event.id}
                                onClick={() => setSelectedEvent(event)}
                                className="w-full flex items-center gap-4 p-3 bg-bg-secondary rounded-lg hover:bg-bg-hover transition-colors text-left"
                            >
                                <div className={clsx(
                                    "w-1.5 h-12 rounded-full flex-shrink-0",
                                    getCategoryColor(event.category)
                                )} />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-text-primary truncate">{event.title}</p>
                                    <p className="text-sm text-text-muted">
                                        {formatDate(event.date)} - {event.time}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </Card>

            {/* Etkinlik Detay Modal */}
            {selectedEvent && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)}>
                    <Card padding="lg" className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-text-primary">Etkinlik Detayı</h3>
                            <button
                                onClick={() => setSelectedEvent(null)}
                                className="p-2 hover:bg-bg-hover rounded-lg transition-colors text-text-tertiary"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <h4 className="text-xl font-bold text-text-primary">{selectedEvent.title}</h4>
                                <span className={clsx(
                                    "inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white mt-2",
                                    getCategoryColor(selectedEvent.category)
                                )}>
                                    {getCategoryLabel(selectedEvent.category)}
                                </span>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-text-secondary">
                                    <Clock size={16} />
                                    <span>{formatDate(selectedEvent.date)} - {selectedEvent.time}</span>
                                </div>
                                {selectedEvent.description && (
                                    <div className="flex items-start gap-3 text-text-secondary">
                                        <MapPin size={16} className="mt-0.5" />
                                        <span>{selectedEvent.description}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 text-text-secondary">
                                    <User size={16} />
                                    <span>Oluşturan: {selectedEvent.createdBy}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6">
                            <button
                                onClick={() => setSelectedEvent(null)}
                                className="w-full py-2 px-4 bg-bg-tertiary text-text-primary rounded-lg hover:bg-bg-hover transition-colors font-medium"
                            >
                                Kapat
                            </button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Etkinlik Ekleme Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
                    <Card padding="lg" className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-text-primary">Yeni Etkinlik</h3>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="p-2 hover:bg-bg-hover rounded-lg transition-colors text-text-tertiary"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Başlık</label>
                                <input
                                    type="text"
                                    value={newEvent.title}
                                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                                    placeholder="Etkinlik başlığı..."
                                    className="w-full px-3 py-2 bg-bg-secondary border border-border-light rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <DatePickerInput
                                    value={newEvent.date}
                                    onChange={(date) => setNewEvent({ ...newEvent, date })}
                                    label="Tarih"
                                    placeholder="Tarih seçin"
                                />
                                <DatePickerInput
                                    value={newEvent.date}
                                    onChange={(date) => setNewEvent({ ...newEvent, date })}
                                    label="Saat"
                                    showTimeOnly
                                    placeholder="Saat seçin"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Kategori</label>
                                <select
                                    value={newEvent.category}
                                    onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                                    className="w-full px-3 py-2 bg-bg-secondary border border-border-light rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    {eventCategories.map(cat => (
                                        <option key={cat.key} value={cat.key}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Açıklama</label>
                                <textarea
                                    value={newEvent.description}
                                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                                    placeholder="Etkinlik detayları..."
                                    rows={3}
                                    className="w-full px-3 py-2 bg-bg-secondary border border-border-light rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex gap-2">
                            <button
                                onClick={handleSaveEvent}
                                className="flex-1 py-2.5 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium min-h-[48px]"
                            >
                                Kaydet
                            </button>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="flex-1 py-2 px-4 bg-bg-tertiary text-text-primary rounded-lg hover:bg-bg-hover transition-colors font-medium"
                            >
                                İptal
                            </button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
